import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { defaultRouteForRole } from '../utils/navigation';

const C = {
  bg: '#0A0E1A', surface: '#111827', border: '#1F2937',
  accent: '#10B981', danger: '#EF4444', text: '#F9FAFB', muted: '#6B7280', input: '#1C2433',
};

export default function Login() {
  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason');
  const sessionExpired = reason === 'session_expired';
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const debugEnabled = params.get('debug') === '1';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (debugEnabled) setDebugInfo(null);
    try {
      const normalized = identifier.trim().toLowerCase();
      let resp;

      try {
        resp = await api.post('/auth/login', { email: normalized, password });
      } catch (firstErr) {
        const firstStatus = firstErr?.response?.status;
        if (firstStatus === 400 || firstStatus === 401) {
          resp = await api.post('/auth/login', { username: normalized, password });
        } else {
          throw firstErr;
        }
      }

      const accessToken = resp?.data?.token || resp?.data?.access_token;
      if (!accessToken) {
        throw new Error('Respuesta de autenticación inválida');
      }

      localStorage.setItem('access_token', accessToken);
      if (resp?.data?.refresh_token) {
        localStorage.setItem('refresh_token', resp.data.refresh_token);
      }

      const previousUser = (() => {
        try {
          return JSON.parse(localStorage.getItem('user') || 'null');
        } catch {
          return null;
        }
      })();

      const normalizedUser = {
        id: resp?.data?.user?.id ?? previousUser?.id ?? null,
        username: resp?.data?.user?.username || previousUser?.username || normalized,
        email: resp?.data?.user?.email || previousUser?.email || null,
        role: resp?.data?.user?.role || previousUser?.role || null,
      };

      localStorage.setItem('user', JSON.stringify(normalizedUser));

      const role = normalizedUser.role;
      const next = new URLSearchParams(window.location.search).get('next');
      const safeNext = next && next.startsWith('/') ? next : null;
      const targetRoute = safeNext || defaultRouteForRole(role);

      if (debugEnabled) {
        setDebugInfo({
          ok: true,
          identifier: normalized,
          role: role || null,
          hasToken: !!accessToken,
          next,
          targetRoute,
        });
      }

      window.location.href = targetRoute;
    } catch (err) {
      const apiError = err.response?.data?.error || 'Credenciales inválidas';
      setError(apiError);
      if (debugEnabled) {
        setDebugInfo({
          ok: false,
          identifier: identifier.trim().toLowerCase(),
          status: err.response?.status || null,
          apiError,
          next: new URLSearchParams(window.location.search).get('next'),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src="/yupay-logo.svg" alt="Yupay Fintech" style={{ width: 220, marginBottom: 8 }} />
          <div style={{ fontSize: 24, fontWeight: 700, color: C.accent }}>Yupay Fintech</div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>Portal de Comercios</div>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 32 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 24 }}>Iniciar sesión</div>

          {sessionExpired && (
            <div style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}66`, borderRadius: 8, padding: '10px 14px', color: C.text, fontSize: 13, marginBottom: 18 }}>
              Tu sesión expiró por seguridad. Inicia sesión nuevamente para continuar.
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 7 }}>Email o usuario</label>
              <input
                type="text" required
                style={{ width: '100%', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                value={identifier} onChange={e => setIdentifier(e.target.value)}
                placeholder="tu@email.com o usuario"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 7 }}>Contraseña</label>
              <input
                type="password" required
                style={{ width: '100%', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Tu contraseña"
              />
            </div>

            {error && (
              <div style={{ background: `${C.danger}22`, border: `1px solid ${C.danger}55`, borderRadius: 8, padding: '10px 14px', color: C.danger, fontSize: 13, marginBottom: 18 }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Verificando...' : 'Entrar'}
            </button>
          </form>

          <div style={{ marginTop: 20, fontSize: 12, color: C.muted, textAlign: 'center' }}>
            ¿No tienes credenciales? Contacta a tu administrador de cooperativa.
          </div>
          <div style={{ marginTop: 8, fontSize: 12, textAlign: 'center' }}>
            <Link to="/" style={{ color: C.accent, textDecoration: 'none' }}>← Volver a inicio</Link>
          </div>

          {debugEnabled && (
            <div style={{ marginTop: 14, background: `${C.accent}18`, border: `1px solid ${C.accent}55`, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: C.text }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Modo diagnóstico</div>
              {!debugInfo ? (
                <div style={{ color: C.muted }}>Sin intento de login aún</div>
              ) : debugInfo.ok ? (
                <div>
                  <div>ok: true</div>
                  <div>identifier: {debugInfo.identifier}</div>
                  <div>role: {String(debugInfo.role)}</div>
                  <div>hasToken: {String(debugInfo.hasToken)}</div>
                  <div>next: {String(debugInfo.next || '')}</div>
                  <div>targetRoute: {debugInfo.targetRoute}</div>
                </div>
              ) : (
                <div>
                  <div>ok: false</div>
                  <div>identifier: {debugInfo.identifier}</div>
                  <div>status: {String(debugInfo.status || '')}</div>
                  <div>apiError: {debugInfo.apiError}</div>
                  <div>next: {String(debugInfo.next || '')}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
